import * as React from 'react';
import sanitizeHtml from 'sanitize-html';
import isNumber from 'lodash/isNumber';
import snakeCase from 'lodash/snakeCase';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum } from 'utils/helper/common';
import { renderReactTooltipToElement } from 'components/graphs/echarts/util/echarts-react-tooltip';

import type {
  LineGraphAxisLabel,
  LineGraphAxisPointer,
  LineGraphFormatterParams,
  LineGraphTooltip,
  LineGraphType
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('utils/graphs/sov-graphs.i18n.json'));

type StringNumber = string | number;
type ArrayArrayMixed = Array<Array<StringNumber>>;

const SANITIZE_HTML_CONFIG = {
  allowedTags: [],
  allowedAttributes: {}
};

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'sov_shared';
const GRAPHS_MSG_KEY = 'sov_graphs';

export const getQueryLabel = (dataType: string, graphType: LineGraphType): string => {
  let specificKey = dataType;

  if (specificKey === 'posts') {
    specificKey = graphType === 'instagram' ? specificKey : 'videos_posted';
  }

  return __t(`${COMMON_MSG_KEY}.kpis.${specificKey}`) || '';
};

export const getFormattedAxisPointerLabel = (value: StringNumber, isSingleQuery: boolean): string => {
  if (isNumber(value)) {
    if (isSingleQuery) {
      return abbrNum(value);
    }

    return `${value.toFixed(2)}%`;
  }

  return value;
};

export const getAxisPointer = (isSingleQuery: boolean): LineGraphAxisPointer => ({
  type: 'cross',

  label: {
    show: true,
    fontFamily: 'Hind',
    fontSize: 14,
    padding: [7, 7, 1],
    backgroundColor: '#3a3a3a',
    formatter: (label: LineGraphAxisLabel): string => getFormattedAxisPointerLabel(label.value, isSingleQuery)
  }
});

export const getFormattedLineItemPercent = (lineItemPercent: number): string => {
  if (lineItemPercent <= 0) {
    return '0%';
  }

  if (lineItemPercent < 0.1) {
    return `${lineItemPercent.toFixed(2)}%`;
  }

  return lineItemPercent < 10 && lineItemPercent !== 0
    ? `${lineItemPercent.toFixed(1)}%`
    : `${Math.round(lineItemPercent)}%`;
};

export const getMarker = (param: LineGraphFormatterParams): React.ReactNode => (
  <div
    className="line-graph__tooltip-marker"
    style={{ backgroundColor: param.color }}
  />
);

export const getTableHead = (isSov: boolean): React.ReactNode => (
  <thead>
    <tr>
      <th className="line-graph__tooltip-th line-graph__tooltip-th--left">
        {__t(`${SHARED_MSG_KEY}.labels.${isSov ? 'keyword' : 'query'}`)}
      </th>
      <th className="line-graph__tooltip-th line-graph__tooltip-th--right">
        {__t(`${COMMON_MSG_KEY}.basics.value`)}
      </th>
      <th className="line-graph__tooltip-th line-graph__tooltip-th--right">
        {__t(`${COMMON_MSG_KEY}.basics.ratio`)}
      </th>
    </tr>
  </thead>
);

export const getTableFoot = (label: string, totalValue: number, totalPercent: number): React.ReactNode => (
  <tfoot>
    <tr>
      <td className="line-graph__tooltip-tfoot-td line-graph__tooltip-tfoot-td--left">
        {__t(`${GRAPHS_MSG_KEY}.all.label_total_number`)} {label.toLowerCase()}
      </td>
      <td className="line-graph__tooltip-tfoot-td">
        {abbrNum(totalValue)}
      </td>
      <td className="line-graph__tooltip-tfoot-td">
        {Math.round(totalPercent)}%
      </td>
    </tr>
  </tfoot>
);

export const getTableRow = (
  param: LineGraphFormatterParams,
  total: number,
  totalLineItemPercent: string
): React.ReactNode => (
  <tr key={param.seriesName}>
    <td className="line-graph__tooltip-td text-overflow-ellipsis">
      <div className="line-graph__tooltip-marker-name text-overflow-ellipsis">
        {getMarker(param)}
        <div className="text-overflow-ellipsis">
          {sanitizeHtml(param.seriesName, SANITIZE_HTML_CONFIG)}
        </div>
      </div>
    </td>
    <td className="line-graph__tooltip-td line-graph__tooltip-td--right">
      {abbrNum(total)}
    </td>
    <td className="line-graph__tooltip-td line-graph__tooltip-td--right">
      {totalLineItemPercent}
    </td>
  </tr>
);

export const getFormattedTooltip = (
  params: Array<LineGraphFormatterParams>,
  totals: ArrayArrayMixed,
  dataType: string,
  graphType: LineGraphType,
  isSingleQuery: boolean,
  isSov: boolean
): React.ReactElement => {
  const tooltipRows: Array<React.ReactNode> = [];
  let totalValue = 0;
  let totalPercent = 0;

  params.forEach(param => {
    let total = totals[Number(param.seriesIndex)][Number(param.dataIndex)];
    let totalLineItemPercent: string | number = 100;

    total = total ? Math.round(Number(total)) : 0;
    totalValue += total;
    totalPercent += Number(param.value);

    totalLineItemPercent = Number(isSingleQuery ? totalLineItemPercent : param.value);
    totalLineItemPercent = getFormattedLineItemPercent(totalLineItemPercent);

    tooltipRows.push(getTableRow(param, total, totalLineItemPercent));
  });

  const label = getQueryLabel(snakeCase(dataType), graphType);

  totalPercent = isSingleQuery ? 100 : totalPercent;

  return (
    <React.Fragment>
      <div className="line-graph__tooltip-title">
        {label}
      </div>
      <table className="line-graph__tooltip-table">
        {getTableHead(isSov)}
        <tbody>
          {tooltipRows}
        </tbody>
        {getTableFoot(label, totalValue, totalPercent)}
      </table>
    </React.Fragment>
  );
};

export const getTooltip = (
  dataType: string,
  graphType: LineGraphType,
  totals: ArrayArrayMixed,
  isSingleQuery: boolean,
  isSov: boolean
): LineGraphTooltip => ({
  trigger: 'axis',
  padding: [10, 20],
  backgroundColor: '#fff',
  borderColor: '#e0e0e0',
  borderWidth: 1,

  textStyle: {
    fontFamily: 'Hind',
    color: '#3a3a3a',
    lineHeight: 20
  },

  axisPointer: getAxisPointer(isSingleQuery),
  position: (mousePosition: Array<number>): Array<number> => [mousePosition[0] + 10, mousePosition[1] + 10],
  formatter: (params: Array<LineGraphFormatterParams>): HTMLElement => (
    renderReactTooltipToElement('line-graph', getFormattedTooltip(params, totals, dataType, graphType, isSingleQuery, isSov))
  ),
  extraCssText: 'box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.2);'
});
